import { Trophy, Minus, Circle, CheckCircle2 } from "lucide-react";

const MatchSlot = ({ match, teamMap, onRecordResult, isActive }) => {
  if (!match) return <div className="h-[52px] min-w-[170px]" />;
  const t1 = teamMap[match.team1_id] || {};
  const t2 = teamMap[match.team2_id] || {};
  const t1Won = match.winner_team_id === match.team1_id;
  const t2Won = match.winner_team_id === match.team2_id;
  const isBye = match.status === "bye";
  const canClick = isActive && !t1Won && !t2Won && !isBye && match.team1_id && match.team2_id;

  // Bye matches: show compact auto-advance label
  if (isBye) {
    const advancer = t1.team_name || t2.team_name || "TBD";
    return (
      <div className="min-w-[170px] border border-white/5 bg-[#0A0A0A]/50 shrink-0 opacity-50" data-testid={`bracket-slot-${match.id}`}>
        <div className="px-2.5 py-1.5 text-xs flex items-center justify-between border-b border-white/5">
          <span className="font-heading font-bold truncate text-[#309F6A]">{advancer}</span>
          <Trophy className="w-3 h-3 text-[#309F6A] shrink-0 ml-1" />
        </div>
        <div className="px-2.5 py-1 text-[9px] text-white/20 italic">BYE</div>
      </div>
    );
  }

  return (
    <div className={`min-w-[170px] border shrink-0 ${canClick ? "border-[#E3292E]/40 bg-[#E3292E]/5" : "border-white/10 bg-[#0A0A0A]"}`} data-testid={`bracket-slot-${match.id}`}>
      <div
        className={`px-2.5 py-1.5 text-xs flex items-center justify-between border-b border-white/5 transition-colors duration-200 ${t1Won ? "bg-[#309F6A]/15" : canClick ? "hover:bg-[#E3292E]/10 cursor-pointer" : ""}`}
        onClick={() => canClick && onRecordResult?.(match.id, match.team1_id)}
      >
        <span className={`font-heading font-bold truncate ${t1Won ? "text-[#309F6A]" : t2Won ? "text-white/30" : "text-white/80"}`}>
          {t1.team_name || "TBD"}
        </span>
        {t1Won && <Trophy className="w-3 h-3 text-[#309F6A] shrink-0 ml-1" />}
      </div>
      <div
        className={`px-2.5 py-1.5 text-xs flex items-center justify-between transition-colors duration-200 ${t2Won ? "bg-[#309F6A]/15" : canClick ? "hover:bg-[#E3292E]/10 cursor-pointer" : ""}`}
        onClick={() => canClick && onRecordResult?.(match.id, match.team2_id)}
      >
        <span className={`font-heading font-bold truncate ${t2Won ? "text-[#309F6A]" : t1Won ? "text-white/30" : "text-white/80"}`}>
          {t2.team_name || "TBD"}
        </span>
        {t2Won && <Trophy className="w-3 h-3 text-[#309F6A] shrink-0 ml-1" />}
      </div>
    </div>
  );
};

const Connector = ({ matchCount }) => {
  if (matchCount <= 1) {
    return (
      <div className="flex items-center w-6 shrink-0">
        <div className="w-full border-t border-white/10" />
      </div>
    );
  }
  return (
    <div className="flex flex-col w-6 shrink-0">
      {Array.from({ length: Math.ceil(matchCount / 2) }).map((_, i) => (
        <div key={i} className="flex flex-col flex-1">
          <div className="flex-1 border-r border-t border-white/10" />
          <div className="flex-1 border-r border-b border-white/10" />
        </div>
      ))}
    </div>
  );
};

export default function BracketDiagram({ matches, teamMap, championId, byeTeamId, divisionName, color, onRecordResult, isActive }) {
  const rounds = {};
  (matches || []).forEach((m) => {
    const r = m.bracket_round;
    if (!rounds[r]) rounds[r] = [];
    rounds[r].push(m);
  });

  // Filter out rounds where ALL matches are byes (no real games)
  const roundKeys = Object.keys(rounds).map(Number).sort((a, b) => a - b)
    .filter(rn => rounds[rn].some(m => m.status !== "bye"));
  const champion = championId ? teamMap[championId] : null;

  if (roundKeys.length === 0 && !champion) return null;

  const getRoundLabel = (idx, total) => {
    const fromEnd = total - idx;
    if (fromEnd === 1) return "FINAL";
    if (fromEnd === 2) return "SEMI-FINAL";
    if (fromEnd === 3) return "QUARTER-FINAL";
    return `ROUND ${idx + 1}`;
  };

  const totalCols = roundKeys.length + (champion ? 1 : 0);

  return (
    <div className="space-y-3" data-testid={`bracket-${divisionName}`}>
      {/* Horizontal bracket - desktop */}
      <div className="overflow-x-auto hidden sm:block">
        <div className="flex items-stretch min-w-fit py-2" style={{ minHeight: `${Math.max(1, rounds[roundKeys[0]]?.length || 1) * 70 + 30}px` }}>
          {roundKeys.map((rn, idx) => (
            <div key={rn} className="flex items-stretch">
              <div className="flex flex-col justify-around shrink-0" style={{ minWidth: "180px", gap: `${Math.pow(2, idx) * 12}px`, padding: "4px 0" }}>
                <p className="text-[10px] tracking-[0.15em] uppercase text-white/30 text-center font-bold">
                  {getRoundLabel(idx, totalCols)}
                </p>
                {rounds[rn].map((match) => (
                  <MatchSlot key={match.id} match={match} teamMap={teamMap} onRecordResult={onRecordResult} isActive={isActive} />
                ))}
              </div>
              {(idx < roundKeys.length - 1 || champion) && (
                <Connector matchCount={rounds[rn].length} />
              )}
            </div>
          ))}
          {champion && (
            <div className="flex flex-col justify-center shrink-0" style={{ minWidth: "150px" }}>
              <p className="text-[10px] tracking-[0.15em] uppercase text-center font-bold mb-2" style={{ color }}>CHAMPION</p>
              <div className="border-2 p-3 text-center" style={{ borderColor: color, backgroundColor: `${color}15` }}>
                <Trophy className="w-6 h-6 mx-auto mb-1" style={{ color }} />
                <p className="font-heading font-bold text-white text-sm">{champion.team_name}</p>
                <p className="text-white/40 text-[10px]">{champion.players?.length ? champion.players.join(", ") : (champion.player2 ? `${champion.player1} & ${champion.player2}` : champion.player1 || "")}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Vertical flow - mobile */}
      <div className="sm:hidden space-y-4">
        {roundKeys.map((rn, idx) => (
          <div key={rn}>
            <p className="text-[10px] tracking-[0.15em] uppercase text-white/30 font-bold mb-2">
              {getRoundLabel(idx, totalCols)}
            </p>
            <div className="space-y-2">
              {rounds[rn].map((match) => (
                <MatchSlot key={match.id} match={match} teamMap={teamMap} onRecordResult={onRecordResult} isActive={isActive} />
              ))}
            </div>
          </div>
        ))}
        {champion && (
          <div className="border-2 p-4 text-center" style={{ borderColor: color, backgroundColor: `${color}15` }}>
            <Trophy className="w-8 h-8 mx-auto mb-2" style={{ color }} />
            <p className="font-heading font-bold text-white">{champion.team_name}</p>
            <p className="text-white/40 text-xs">{champion.players?.length ? champion.players.join(", ") : (champion.player2 ? `${champion.player1} & ${champion.player2}` : champion.player1 || "")}</p>
          </div>
        )}
      </div>

      {byeTeamId && teamMap[byeTeamId] && !championId && (
        <p className="text-white/30 text-xs font-body">
          <Minus className="w-3 h-3 inline mr-1" />
          Bye: <span className="text-white/50 font-heading font-bold">{teamMap[byeTeamId].team_name}</span> (auto-advances)
        </p>
      )}
    </div>
  );
}

export function TournamentFlowStepper({ event, tournamentPhase }) {
  const steps = [
    { key: "round_robin", label: "ROUND-ROBIN", detail: event?.current_round && event?.total_rounds ? `Round ${event.current_round}/${event.total_rounds}` : "" },
    { key: "divisions", label: "DIVISIONS", detail: "Knockout brackets" },
    { key: "complete", label: "CHAMPIONS", detail: "Tournament complete" },
  ];

  const currentIdx = steps.findIndex((s) => s.key === tournamentPhase) ?? 0;
  const isSocial = event?.event_type !== "tournament";

  if (isSocial) {
    const rr = event?.round_robin_complete;
    return (
      <div className="flex items-center gap-2 py-3 px-4 border border-white/10 bg-[#141414] mb-6" data-testid="flow-stepper">
        <div className="flex items-center gap-1.5">
          <CheckCircle2 className="w-4 h-4 text-[#309F6A]" />
          <span className="text-xs tracking-[0.1em] uppercase font-bold text-white/70">Round-Robin</span>
        </div>
        <div className="flex-1 border-t border-white/10 mx-2" />
        <div className="flex items-center gap-1.5">
          {rr ? <CheckCircle2 className="w-4 h-4 text-[#309F6A]" /> : <Circle className="w-4 h-4 text-white/20" />}
          <span className={`text-xs tracking-[0.1em] uppercase font-bold ${rr ? "text-white/70" : "text-white/30"}`}>Open Play</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 py-3 px-4 border border-white/10 bg-[#141414] mb-6 overflow-x-auto" data-testid="flow-stepper">
      {steps.map((step, idx) => {
        const isDone = idx < currentIdx;
        const isCurrent = idx === currentIdx;
        return (
          <div key={step.key} className="flex items-center gap-2 shrink-0">
            {idx > 0 && <div className={`w-8 border-t-2 ${isDone ? "border-[#309F6A]" : "border-white/10"}`} />}
            <div className="flex items-center gap-1.5">
              {isDone ? (
                <CheckCircle2 className="w-4 h-4 text-[#309F6A]" />
              ) : isCurrent ? (
                <div className="w-4 h-4 rounded-full border-2 border-[#E3292E] bg-[#E3292E]/20 animate-pulse" />
              ) : (
                <Circle className="w-4 h-4 text-white/20" />
              )}
              <div>
                <span className={`text-xs tracking-[0.1em] uppercase font-bold ${isDone ? "text-[#309F6A]" : isCurrent ? "text-white" : "text-white/30"}`}>
                  {step.label}
                </span>
                {isCurrent && step.detail && (
                  <span className="text-[10px] text-white/40 ml-1.5">{step.detail}</span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
